import { IProductListItem } from '@/api/types/product';
import Image from '@/components/Image';
import MagicJump, { JumpTarget } from '@/components/MagicJump';
import { useStore } from '@/providers/app-store-provider';
import { colors } from '@/theme/zeelool.theme';
import { getHtmlText } from '@/utils/goods-list-utils';
import { isEmpty, setSession } from '@/utils/tools';
import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

export default function AdvCard({
  product,
  sdProductListPageADClick,
  sdProductListPageADExp,
  column = 2,
  isWeb = false,
  index,
  isCrowdRecommendation = false,
}: {
  product: IProductListItem;
  sdProductListPageADClick: (product: any, index: number) => void;
  sdProductListPageADExp: (product: any, index: number) => void;
  column?: number;
  isWeb?: boolean;
  index: number;
  isCrowdRecommendation?: boolean;
}) {
  const isWideAdv = product.type === 2 || column === 1;

  const { waiMode } = useStore((state) => state);

  const [adRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      sdProductListPageADExp(product, index);
    }
  }, [inView]);

  const jumpInfo: JumpTarget = useMemo(() => {
    const { pits_jump_info } = product || {};
    if (isEmpty(pits_jump_info)) {
      return { jump_type: 0, Jump_target: '' };
    }
    return pits_jump_info;
  }, [product]);

  const clickBanner = () => {
    sdProductListPageADClick(product, index);
    const adSceneName = isCrowdRecommendation ? '人群推荐列表页广告坑位' : '商品列表页广告坑位';

    if (jumpInfo.jump_type === 1) {
      setSession('zl_goodsListEnterPageScene', '商品列表页广告坑位');
    }
    if (jumpInfo.jump_type === 2) {
      setSession('zl_goodsDetailEnterPageScene', adSceneName);
    }
  };

  // 合并所有样式计算逻辑
  const styleClasses = useMemo(() => {
    // 使用对象映射的方式优化类名配置
    const styleConfig = {
      container: {
        web: {
          wide: 'col-span-2 min-h-[365px]',
          normal: 'col-span-1',
        },
        mobile: {
          wide: `col-span-2 h-[69.6vw] ${column === 2 ? 'px-14' : ''}`,
          normal: 'col-span-1',
        },
      },
      imgWrap: {
        web: {
          wide: 'relative h-full w-full max-[640px]:aspect-[540/500]',
          normal: 'relative h-full w-full max-[640px]:aspect-[540/500]',
        },
        mobile: {
          wide: 'relative h-full w-full',
          normal: 'relative h-[100%]',
        },
      },
      bannerImg: {
        web: {
          wide: 'rounded-16',
          normal: 'rounded-12',
        },
        mobile: {
          wide: 'rounded-16',
          normal: 'rounded-0',
        },
      },
      bannerText: {
        web: {
          wide: 'absolute top-1/2 -translate-y-1/2 z-1 !w-[33.3%] !pl-48 max-[640px]:!w-[100%] max-[640px]:!pl-0 max-[640px]:text-center box-content',
          normal: 'absolute left-1/2 -translate-x-1/2 bottom-[97px] z-1 w-full text-center',
        },
        mobile: {
          wide:
            product.type === 2
              ? 'absolute top-1/2 -translate-y-1/2 z-1 w-[136px] pl-20' // 双坑位广告位占据一行时的文字样式
              : 'absolute bottom-[48px] z-1 px-[88px] text-center w-full', // 单坑位广告位占据一行时的文字样式
          normal: 'absolute bottom-[52px] z-1 px-[8px] text-center',
        },
      },
    };

    // 获取当前配置的类名
    let advType = 'normal';
    if (isWideAdv) {
      advType = 'wide';
    }

    let platform = 'mobile';
    if (isWeb) {
      platform = 'web';
    }

    const containerClass = styleConfig.container[platform][advType];
    const imgWrapClass = styleConfig.imgWrap[platform][advType];
    const bannerImgClass = styleConfig.bannerImg[platform][advType];
    const bannerTextClass = styleConfig.bannerText[platform][advType];
    return {
      container: `gg-banner relative overflow-hidden ${containerClass}`,
      imgWrap: `product-img-wrap relative flex flex-col items-center ${imgWrapClass}`,
      bannerImg: `banner_img absolute w-full h-full object-cover z-1 ${bannerImgClass}`,
      bannerText: `banner_text ${bannerTextClass}`,
      imgDimensions: {
        width: isWideAdv ? 347 : 186,
        height: isWideAdv ? 261 : 301,
      },
      imageDimensionsWeb: {
        width: isWideAdv ? 1024 : 347,
        height: isWideAdv ? 261 : 261,
      },

      wideBannerButton: `
        ${isWideAdv ? 'visible' : 'hidden'}
        ${isWeb ? 'block px-24 py-12 text-primary-black rounded-20 text-14 font-bb bg-white overflow-hidden whitespace-nowrap text-ellipsis mt-32 w-max max-w-[100%]' : ''}
        ${isWeb ? 'max-[640px]:mx-auto' : ''}
        ${!isWeb ? `h-32 rounded-16 px-24 leading-32 text-14 overflow-hidden whitespace-nowrap text-ellipsis max-w-[133px] w-fit mt-[23px] block` : ''}
        ${!isWeb && column === 1 ? 'mx-auto' : ''}
        `,
    };
  }, [isWideAdv, column, isWeb]);

  return (
    <MagicJump
      prefetch
      item={jumpInfo}
      className={styleClasses.container}
      onBeforeJump={clickBanner}
      ref={adRef}
    >
      <div className={styleClasses.imgWrap}>
        <Image
          src={product.img_url}
          className={styleClasses.bannerImg}
          alt={product.pits_title || ''}
          height={
            isWeb ? styleClasses.imageDimensionsWeb.height : styleClasses.imgDimensions.height
          }
          width={isWeb ? styleClasses.imageDimensionsWeb.width : styleClasses.imgDimensions.width}
          fetchPriority="high"
        />
        {product.pits_btn_text && product.type === 1 && (
          <section
            className={`
             banner_button z-1 absolute left-1/2 -translate-x-1/2  w-max
             ${isWeb ? 'h-40 px-24 font-bb text-14 leading-40 rounded-20 bottom-[33px] max-w-[85%] text-center overflow-hidden whitespace-nowrap text-ellipsis' : 'min-w-[100px] max-w-[85%] text-center bottom-12 h-28 px-[21px] leading-28 rounded-14 text-12 overflow-hidden whitespace-nowrap text-ellipsis'}`}
            style={{
              backgroundColor: product.btn_bg_color || '#ffffff',
              color: product.btn_word_color || '#282828',
            }}
          >
            {product.pits_btn_text || 'Shop Now'}
          </section>
        )}
      </div>
      <div
        className={styleClasses.bannerText}
        style={{ color: waiMode ? colors.primaryBlack : product.pits_text_color }}
      >
        {product.pits_title && (
          <p
            className={`banner_title font-bb pre-line ${isWeb ? 'text-32 leading-[39px]' : 'text-[16px] leading-[20px] overflow-hidden text-ellipsis line-clamp-5'}`}
            dangerouslySetInnerHTML={{ __html: getHtmlText(product.pits_title) }}
          />
        )}
        {product.pits_desc && (
          <p
            className={`banner_desc ${isWeb ? 'text-14 mt-12 leading-[18px]' : 'mt-[4px] text-[11px] leading-[16px] overflow-hidden text-ellipsis line-clamp-8'}`}
            dangerouslySetInnerHTML={{ __html: getHtmlText(product.pits_desc) }}
          />
        )}
        {/* 只有type为2的广告才显示按钮 */}
        {product.pits_btn_text && product.type === 2 && (
          <section
            className={`
             banner_button
             ${styleClasses.wideBannerButton}
            `}
            style={{
              backgroundColor: product.btn_bg_color || '#ffffff',
              color: product.btn_word_color || '#282828',
            }}
            dangerouslySetInnerHTML={{ __html: getHtmlText(product.pits_btn_text) }}
          />
        )}
      </div>
    </MagicJump>
  );
}
